import React from "react";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { fadeUp, revealProps, stagger, useReducedMotion } from "homepage-new/motion";
import { rf } from "../theme";
import { Band, Card, Display, Pill, PushButton } from "../ui";
import {
  byRelevance,
  daysUntil,
  durationWeeks,
  fillOf,
  formatDate,
  formatSchedule,
  money,
  priceOf,
  statusOf,
} from "../cohortStatus";

const Fact = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontFamily: rf.display, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: rf.inkFaint, mb: 0.3 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, fontWeight: 600, color: rf.ink }}>{value}</Typography>
  </Box>
);

const CohortCard = ({ cohort, onAction, onSyllabus }) => {
  const status = statusOf(cohort);
  const closesIn = daysUntil(cohort.registration_closes_on);
  const price = priceOf(cohort);
  const weeks = durationWeeks(cohort);

  return (
    <Card sx={{ p: { xs: 2.5, md: 3.25 }, height: "100%", display: "flex", flexDirection: "column", textAlign: "left" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Pill bg={fillOf(cohort)} sx={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 11 }}>
          {status.label}
        </Pill>
        <Typography sx={{ fontFamily: rf.body, fontSize: 12.5, fontWeight: 600, color: rf.inkFaint }}>{cohort.level}</Typography>
      </Box>

      <Display component="h3" size={26} sx={{ mb: 1 }}>
        {cohort.title}
      </Display>
      <Typography sx={{ fontFamily: rf.display, fontSize: 15.5, fontWeight: 700, color: rf.coralDeep, mb: 1.25 }}>
        {cohort.tagline}
      </Typography>
      <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, color: rf.inkSoft, lineHeight: 1.6, mb: 2.5 }}>{cohort.description}</Typography>

      <Box sx={{ p: 2, backgroundColor: rf.cream, border: rf.lineThin, borderRadius: rf.radiusSm }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Fact label="Starts" value={formatDate(cohort.start_date)} />
          </Grid>
          <Grid item xs={6}>
            <Fact label="Runs for" value={`${weeks} weeks`} />
          </Grid>
          <Grid item xs={12}>
            <Fact label="Live sessions" value={formatSchedule(cohort)} />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap" }}>
          <Display component="p" size={32}>
            {money(price.payable, price.currency)}
          </Display>
          {price.discountLive && (
            <Typography sx={{ fontFamily: rf.body, fontSize: 15, color: rf.inkFaint, textDecoration: "line-through" }}>
              {money(price.listed, price.currency)}
            </Typography>
          )}
        </Box>

        {price.discountLive && price.discountDaysLeft > 0 && (
          <Pill bg={rf.amber} sx={{ mt: 1.25, fontSize: 12 }}>
            Offer ends in {price.discountDaysLeft} day{price.discountDaysLeft === 1 ? "" : "s"} ·{" "}
            {formatDate(price.discount_ends_on)}
          </Pill>
        )}
        {status.action === "enroll" && closesIn > 0 && (
          <Typography sx={{ fontFamily: rf.body, fontSize: 13.5, color: rf.inkSoft, mt: 1 }}>
            Registrations close {formatDate(cohort.registration_closes_on)}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: "auto", pt: 2.75, display: "flex", gap: 1.25, flexWrap: "wrap" }}>
        <PushButton variant={status.action === "enroll" ? "primary" : "dark"} pulse onClick={() => onAction(cohort)}>
          {status.actionLabel}
        </PushButton>
        <PushButton variant="light" onClick={() => onSyllabus(cohort)}>
          See the syllabus
        </PushButton>
      </Box>
    </Card>
  );
};

const CardSkeleton = () => (
  <Card sx={{ p: 3.25 }}>
    <Skeleton variant="rounded" width={140} height={26} sx={{ mb: 2 }} />
    <Skeleton variant="text" width="70%" height={36} />
    <Skeleton variant="text" width="90%" />
    <Skeleton variant="rounded" height={120} sx={{ mt: 2.5 }} />
    <Skeleton variant="rounded" height={46} width={200} sx={{ mt: 3 }} />
  </Card>
);

export const Cohorts = ({ cohorts = [], loading, error, onRetry, onAction, onSyllabus }) => {
  const reduced = useReducedMotion();
  const ordered = [...cohorts].sort(byRelevance);

  return (
    <Band
      id="cohorts"
      band
      eyebrow="Running now"
      title="The cohorts currently open"
      lead="Batches are capped and dated. What you see here is what is true today — when a cohort fills or starts, this page says so rather than quietly taking your money."
    >
      {loading && (
        <Grid container spacing={3}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && error && (
        <Card bg={rf.paper} sx={{ p: { xs: 3, md: 5 }, textAlign: "center", maxWidth: 620, mx: "auto" }}>
          <Display component="p" size={28} sx={{ mb: 1.5 }}>
            We couldn't load the cohorts.
          </Display>
          <Typography sx={{ fontFamily: rf.body, fontSize: 15.5, color: rf.inkSoft, mb: 3 }}>
            That's on us, not you. Try again, or write to us and we'll tell you what's running.
          </Typography>
          <PushButton variant="primary" onClick={onRetry}>
            Try again
          </PushButton>
        </Card>
      )}

      {!loading && !error && ordered.length === 0 && (
        <Card bg={rf.amber} sx={{ p: { xs: 3, md: 5 }, textAlign: "center", maxWidth: 620, mx: "auto" }}>
          <Display component="p" size={28} sx={{ mb: 1.5 }}>
            No cohort is open at the moment.
          </Display>
          <Typography sx={{ fontFamily: rf.body, fontSize: 15.5, color: rf.ink }}>
            Nothing is taking registrations today. Leave your details on any running cohort and you'll hear when the next batch opens.
          </Typography>
        </Card>
      )}

      {!loading && !error && ordered.length > 0 && (
        <motion.div variants={stagger(0.08)} {...revealProps(reduced)}>
          <Grid container spacing={3} alignItems="stretch">
            {ordered.map((cohort) => (
              <Grid item xs={12} md={6} key={cohort.slug}>
                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                  <CohortCard cohort={cohort} onAction={onAction} onSyllabus={onSyllabus} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      )}
    </Band>
  );
};

export default Cohorts;
